import React from 'react';

/**
 * <StatusDot />
 *
 * Universal status indicator. Replaces 3 different pill styles across
 * VendorHealthStrip, ApolloSpendWidget, and Tenants table.
 *
 * status: 'ok' | 'degraded' | 'down' | 'warning' | 'idle' | 'paused' | 'unknown'
 * variant: 'dot' (default) | 'pill' (with text label)
 */
const STATUS_MAP = {
  ok:       { color: 'bg-emerald-500',  ring: 'ring-emerald-500/30', label: 'Operational' },
  active:   { color: 'bg-emerald-500',  ring: 'ring-emerald-500/30', label: 'Active' },
  enabled:  { color: 'bg-emerald-500',  ring: 'ring-emerald-500/30', label: 'Enabled' },
  degraded: { color: 'bg-amber-500',    ring: 'ring-amber-500/30',   label: 'Degraded' },
  warning:  { color: 'bg-amber-500',    ring: 'ring-amber-500/30',   label: 'Warning' },
  pending:  { color: 'bg-amber-500',    ring: 'ring-amber-500/30',   label: 'Pending' },
  down:     { color: 'bg-rose-500',     ring: 'ring-rose-500/30',    label: 'Down' },
  error:    { color: 'bg-rose-500',     ring: 'ring-rose-500/30',    label: 'Error' },
  paused:   { color: 'bg-rose-500',     ring: 'ring-rose-500/30',    label: 'Paused' },
  failed:   { color: 'bg-rose-500',     ring: 'ring-rose-500/30',    label: 'Failed' },
  idle:     { color: 'bg-slate-400',    ring: 'ring-slate-400/30',   label: 'Idle' },
  unknown:  { color: 'bg-slate-500',    ring: 'ring-slate-500/30',   label: 'Unknown' },
  info:     { color: 'bg-indigo-500',   ring: 'ring-indigo-500/30',  label: 'Info' },
  not_configured: { color: 'bg-slate-600', ring: 'ring-slate-600/30', label: 'Not configured' },
};

export default function StatusDot({
  status = 'unknown',
  label,
  variant = 'dot',
  size = 'sm',
  pulse = false,
  className = '',
  testid,
}) {
  const cfg = STATUS_MAP[status] || STATUS_MAP.unknown;
  const dotSize = size === 'xs' ? 'h-1.5 w-1.5' : size === 'lg' ? 'h-3 w-3' : 'h-2 w-2';
  const displayLabel = label ?? cfg.label;

  if (variant === 'dot') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${className}`}
        data-testid={testid || `gov-status-${status}`}
      >
        <span
          className={`${dotSize} rounded-full ${cfg.color} ${pulse ? 'animate-pulse' : ''} ring-2 ${cfg.ring}`}
          aria-hidden
        />
        <span className="gov-text-secondary text-xs">{displayLabel}</span>
      </span>
    );
  }

  // pill variant
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-[var(--gov-border)] bg-[var(--gov-bg-elevated)] px-2 py-0.5 text-[11px] font-medium ${className}`}
      data-testid={testid || `gov-status-pill-${status}`}
    >
      <span className={`${dotSize} rounded-full ${cfg.color} ${pulse ? 'animate-pulse' : ''}`} aria-hidden />
      <span className="gov-text-primary">{displayLabel}</span>
    </span>
  );
}
